import { createContext } from 'react';
import { generatePath } from 'react-router-dom';

import { Button } from '@components/button';
import { FullscreenPreview } from '@components/form-builder/fullscreen-preview';
import { mergePredefinedOptions } from '@components/form-builder/utils';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyForm } from 'api/journey-forms/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';

interface PreviewContextType {
  setPreviewJourneyFormId: (id: string) => void;
}

export const PreviewContext = createContext<PreviewContextType>({
  setPreviewJourneyFormId: () => {},
});

export const SelectedFormTemplatePreview = ({
  shortId,
  journeyId,
  onClose,
}: {
  shortId: string;
  journeyId: string;
  onClose(): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { workspaceId } = useCurrentWorkspace();
  const { data: lists } = useFormBuilderLists();
  const formTemplateQuery = useJourneyForm(journeyId, shortId);
  if (!formTemplateQuery.isSuccess) return null;

  const form = formTemplateQuery.data.fieldsConfig;

  const fields = mergePredefinedOptions(form.fields, lists);
  const name = formTemplateQuery.data.name;

  const builderPath =
    generatePath(routePaths.editJourney, {
      workspaceId,
      journeyId,
      '*': 'journey',
    }) + `?openJourneyForm=${shortId}`;

  return (
    <FullscreenPreview onClose={onClose} name={name} fields={fields}>
      <Button
        variant="secondary"
        iconLeft={<IconOpenNewTab />}
        text={t('openInBuilder')}
        href={builderPath}
        target="_blank"
      />
    </FullscreenPreview>
  );
};

const locale = {
  en: {
    openInBuilder: 'Open in form builder',
    removeTitle: 'Delete external form',
    removeDescription: 'Are you sure you want to delete the external form: {name}?',
  },
};
